import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

/**
 * Component to handle automatic logout after 1 hour of inactivity
 * Works for both admin and staff users
 */
export function InactivityLogout() {
    const navigate = useNavigate();
    const location = useLocation();
    const timeoutRef = useRef(null);
    const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds

    const isAuthenticated = () => {
        return !!localStorage.getItem('auth_token');
    };

    const resetTimer = () => {
        // Clear existing timer
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Only set timer if user is authenticated
        if (isAuthenticated()) {
            // Set new timer
            timeoutRef.current = setTimeout(() => {
                handleLogout();
            }, INACTIVITY_TIMEOUT);
        }
    };

    const handleLogout = async () => {
        try {
            // Try to logout via API
            await api.post('/logout').catch(() => {
                // Ignore errors if token is already expired
            });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Clear local storage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            
            // Determine redirect path based on current location
            const currentPath = location.pathname;
            if (currentPath.startsWith('/pilot')) {
                navigate('/pilot/login');
            } else {
                navigate('/login');
            }
            
            // Reload to clear any state
            window.location.reload();
        }
    };

    useEffect(() => {
        if (!isAuthenticated()) {
            // Clear timer if user is not authenticated
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            return;
        }

        // Events that indicate user activity
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        
        // Set initial timer
        resetTimer();

        // Add event listeners
        events.forEach(event => {
            document.addEventListener(event, resetTimer, true);
        });

        // Cleanup
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            events.forEach(event => {
                document.removeEventListener(event, resetTimer, true);
            });
        };
    }, [location.pathname]); // Reset timer when route changes

    return null; // This component doesn't render anything
}
